/** Calculates map padding from the owning canvas/sidebar viewport rectangles. */
export function mapFitPadding(canvas, sidebar, padding = 40) {
  const result = { top: padding, bottom: padding, left: padding, right: padding };
  if (!canvas || !sidebar) {
    return result;
  }
  const rightSide = (sidebar.left + sidebar.right) / 2 > (canvas.left + canvas.right) / 2;
  const overlaps =
    sidebar.left < canvas.right &&
    sidebar.right > canvas.left &&
    sidebar.top < canvas.bottom &&
    sidebar.bottom > canvas.top;
  const overlap = overlaps ? Math.min(canvas.right, sidebar.right) - Math.max(canvas.left, sidebar.left) : 0;
  result.left = rightSide ? 0 : overlap + padding;
  result.right = rightSide ? overlap + padding : 0;
  return result;
}

// Milliseconds a fit may hold the zoom cap when the map reports no idle.
const CAP_RELEASE_FALLBACK = 1000;
const activeCaps = new WeakMap();

/**
 * Fits bounds with the zoom capped until the map settles, then restores the previous cap. Google
 * applies a fit after `fitBounds` returns, so the cap is released on the next idle, or after a
 * second when the camera did not move and no idle comes.
 */
export function fitMapBounds({ maps, map, bounds, canvas, sidebar, maxZoom = 15 }) {
  activeCaps.get(map)?.();
  const previous = map.get("maxZoom") ?? null;
  let idle;
  let fallback;
  function release() {
    if (activeCaps.get(map) !== release) {
      return;
    }
    activeCaps.delete(map);
    idle?.remove();
    clearTimeout(fallback);
    map.setOptions({ maxZoom: previous });
  }
  activeCaps.set(map, release);
  map.setOptions({ maxZoom: previous === null ? maxZoom : Math.min(maxZoom, previous) });
  idle = maps.event.addListenerOnce(map, "idle", release);
  fallback = setTimeout(release, CAP_RELEASE_FALLBACK);
  try {
    map.fitBounds(bounds, mapFitPadding(canvas, sidebar));
  } catch (error) {
    release();
    throw error;
  }
}
